import {
	convertToFourPointScale,
	getGradeColor,
	getGPAColor,
	getCreditColor,
	getAPScoreColor,
	gradePeriodSortKey,
	gradeRecordRecencyMs
} from '../../utils/gradeUtils'

/**
 * Get columns for Lesson List DataTable
 * @param {string} studentId - Student ID
 * @param {boolean} isStudent - Whether the user is a student
 * @param {string} role - User role
 * @param {string} layout - Layout type
 * @param {function} handleDelete - Delete handler function
 * @param {boolean} isProfile - Whether this is for profile view (read-only)
 * @returns {array} Column definitions for DataTable
 */
export const getLessonColumns = (studentId, isStudent, role, layout, handleDelete, handleEdit, isProfile = false, grades = []) => {
	/**
	 * Auto Grade: show the grade from the row that was last saved in the system (updated_at / created_at).
	 * If timestamps are missing, tie-break with GPA-Grades 2 period order, then higher id (newer insert).
	 */
	const getLatestGrade = (lessonId) => {
		const lessonGrades = grades.filter((g) => g.lesson === lessonId)
		if (lessonGrades.length === 0) return 0
		lessonGrades.sort((a, b) => {
			const ta = gradeRecordRecencyMs(a)
			const tb = gradeRecordRecencyMs(b)
			if (tb !== ta) return tb - ta
			const pa = gradePeriodSortKey(a.month)
			const pb = gradePeriodSortKey(b.month)
			if (pb !== pa) return pb - pa
			return (b.id || 0) - (a.id || 0)
		})
		return lessonGrades[0].grade
	}

	const columns = [
	{
		name: 'Year',
		selector: (row) => row.courseyear,
		sortable: true,
		width: '70px'
	},
	{
		name: 'Department',
		selector: (row) => row.department,
		sortable: true,
		width: '100px'
	},
	{
		name: 'Course',
		selector: (row) => row.coursename,
		sortable: true,
		grow: 2,
		cell: (row) => (
			<div>
				{row.coursename}
				{row.autograde && (
					<i className="fa fa-refresh text-primary ms-2" title="Auto Graded"></i>
				)}
			</div>
		)
	},
	{
		name: 'Level',
		selector: (row) => row.level,
		sortable: true,
		width: '80px',
		right: true
	},
	{
		selector: (row) => (row.autograde ? getLatestGrade(row.id) : row.grade),
		sortable: true,
		width: '80px',
		right: true,
		cell: (row) => {
			const displayGrade = row.autograde ? getLatestGrade(row.id) : row.grade
			return (
				<span
					className="badge"
					style={{
						backgroundColor: getGradeColor(displayGrade),
						color: 'white',
						padding: '4px 8px',
						borderRadius: '6px',
						fontSize: '0.85rem'
					}}
					title={row.autograde ? `Auto Grade (most recently saved): ${displayGrade}` : `Grade: ${displayGrade}`}
				>
					{displayGrade}
				</span>
			)
		}
	},
	{
		selector: (row) => {
			const displayGrade = row.autograde ? getLatestGrade(row.id) : row.grade
			return convertToFourPointScale(displayGrade)
		},
		sortable: true,
		width: '80px',
		right: true,
		cell: (row) => {
			const displayGrade = row.autograde ? getLatestGrade(row.id) : row.grade
			const gpa = convertToFourPointScale(displayGrade)
			return (
				<span
					className="badge"
					style={{
						backgroundColor: getGPAColor(gpa),
						color: 'white',
						padding: '4px 8px',
						borderRadius: '6px',
						fontSize: '0.85rem'
					}}
					title={`GPA: ${gpa}`}
				>
					{gpa}
				</span>
			)
		}
	},
	{
		name: 'Credit',
		selector: (row) => row.credit,
		sortable: true,
		width: '80px',
		right: true,
		cell: (row) => (
			<span
				className="badge"
				style={{
					backgroundColor: getCreditColor(row.credit),
					color: 'white',
					padding: '4px 8px',
					borderRadius: '6px',
					fontSize: '0.85rem'
				}}
				title={`Credit: ${row.credit}`}
			>
				{row.credit}
			</span>
		)
	},
	{
		name: 'AP Score',
		selector: (row) => row.apscore,
		sortable: true,
		width: '90px',
		right: true,
		cell: (row) => (
			<span
				className="badge"
				style={{
					backgroundColor: getAPScoreColor(row.apscore),
					color: 'white',
					padding: '4px 8px',
					borderRadius: '6px',
					fontSize: '0.85rem'
				}}
				title={`AP Score: ${row.apscore}`}
			>
				{row.apscore || '-'}
			</span>
		)
	},
	{
		name: 'GPA Calc',
		selector: (row) => (row.isgpacalc ? 'Yes' : 'No'),
		sortable: true,
		width: '100px',
		right: true
	}
	]

	// Add Actions column only if not in profile view
	if (!isProfile) {
		columns.push({
			name: 'Actions',
			grow: 1,
			right: true,
			cell: (row) => (
				<div className="d-flex gap-1">
					<button
						onClick={() => handleEdit(row)}
						className="btn btn-success btn-sm"
					>
						<i className="fa fa-edit me-1"></i>
					</button>
					<button onClick={() => handleDelete(row.id)} className="btn btn-danger btn-sm">
						<i className="fa fa-trash"></i>
					</button>
				</div>
			),
			ignoreRowClick: true
		})
	}

	return columns
}
